import React, { useState } from "react";
import TaskDynamicFields from "../../../../../components/DynamicField/Tasks/TaskDynamicFields";
import { Button, Form } from "react-bootstrap";
import { MentionsInput, Mention } from "react-mentions";
import {
  ToBase64,
  browseFile,
  checkInvalidFile,
  deleteBrowseFile,
  dragEnter,
  dragLeave,
  dragOver,
  drop,
  getUsersMatchingQuery,
  isImageFile,
  mentionDisplayTransform,
  setNewCommentState,
  shortenFileName,
  validFileTypes,
} from "../../../../../util/file-helpers";
import MentionUserItem from "../../tasks-comments/MentionUserItem/MentionUserItem";
import DisplayAlert from "../../tasks-comments/DisplayAlert/DisplayAlert";
import { isDynamicFieldsValid } from "../../../../../components/DynamicField/Tasks/utils/utils";
import {
  addTaskDocumentComment,
  addTaskRegularComment,
} from "../../../../../services/api/task-api";
import { scrollToTop } from "../../../../../util/general-helpers";
import * as dynamicFieldsService from "../../../../../services/api/dynamic-fields-api";
import PropTypes from 'prop-types';
import "./task-create-edit-comments.scss";

const TaskCreateEditComments = ({
  projectTypeId,
  taskTypeId,
  dynamicFields,
  setDynamicFields,
  touched,
  taskMembers,
  newCommentText,
  setNewCommentText,
  selectedFiles,
  setSelectedFiles,
  disabled,
  assignID,
  type,
  setAlertConfig,
  alertConfig,
  tags,
  setRefreshCommentsTable,
  setRefreshStatus,
}) => {
  const [taggedUsers, setTaggedUsers] = useState([]); // List of tagged users
  const [loading, setLoading] = useState(false);
  const handleAddTaggedUser = (id, display) =>
    setTaggedUsers((prevTaggedUsers) => [...prevTaggedUsers, display]);

  const isCommentsEmpty =
    (newCommentText === "" || !/\S/.test(newCommentText)) &&
    selectedFiles.length === 0;
  const isSubmitDisabled =
    isCommentsEmpty && !isDynamicFieldsValid(dynamicFields);

  const uploadFile = async () => {
    setLoading(true);
    const newComment = setNewCommentState(newCommentText);

    if (selectedFiles.length === 0) {
      await saveComment(newComment);
    } else {
      const convertedFiles = await Promise.all(
        selectedFiles.map(async (file) => {
          if (
            !validFileTypes.some((type) =>
              file.name.toLowerCase().includes(type)
            )
          ) {
            setAlertConfig((prev) => ({
              ...prev,
              showAlert: true,
              alertAction: "Some invalid file types",
              alertMessage: "",
              alertVariant: "warning",
            }));
            return null;
          } else if (file.size > 52428800 && !isImageFile(file)) {
            setAlertConfig((prev) => ({
              ...prev,
              showAlert: true,
              alertAction: "Some files exceed 50MB",
              alertMessage: "",
              alertVariant: "warning",
              sizeExceeded: "50MB",
            }));
            return null;
          } else if (file.size > 10485760 && isImageFile(file)) {
            setAlertConfig((prev) => ({
              ...prev,
              showAlert: true,
              alertAction: "Some files exceed 10MB",
              alertMessage: "",
              alertVariant: "warning",
              sizeExceeded: "10MB",
            }));
            return null;
          } else {
            setAlertConfig((prev) => ({
              showAlert: false,
            }));
            const result = await ToBase64(file);
            let mimetype = result.substr(
              result.indexOf(":") + 1,
              result.indexOf(";") - result.indexOf(":") - 1
            );
            let thedocAsBytes = result.split(",")[1];
            return {
              FileName: file.name,
              FileContentType: mimetype,
              FileByteArray: thedocAsBytes,
              TaskId: assignID,
            };
          }
        })
      );

      
      setSelectedFiles([]);
      await saveComment(
        newComment,
        convertedFiles.filter((file) => file !== null)
      );
    }
    setNewCommentText("");
    setRefreshStatus(true);
  };

  const saveComment = async (newComment, convertedFiles = []) => {
    let commentPayload = {
      Note: newComment,
      TaskId: assignID,
      Files: convertedFiles,
      ActionId: 7,
    };

    let taggedUsersList = taggedUsers.filter(
      (user, index) =>
        taggedUsers.indexOf(user) === index && newComment.includes(user)
    );
    commentPayload.taggedUsers = taggedUsersList;

    try {
      if (type === "standard") {
        const dynamicForm = dynamicFields.length > 0;
        if (dynamicForm) {
          const hasData = dynamicFields.some(
            (field) => field.Data !== "" && field.Data !== null
          );
          commentPayload.ActionId = hasData ? 13 : 7;
        }

        const response = await addTaskRegularComment({
          ...commentPayload,
          dynamicForm,
        });
        if (dynamicForm && response?.data?.Comments.length > 0) {
          const body = dynamicFields.map((field) => ({
            DynamicFieldId: field.Id,
            Data: field.Data,
            ObjectId: response?.data?.Comments[0]?.Id,
          }));
          await dynamicFieldsService.saveDynamicFields(body);
          //resetting dynamic fields
          setDynamicFields((prevFields) =>
            prevFields.map((field) => ({ ...field, Data: "" }))
          );
        }
        setRefreshCommentsTable((prev) => prev + 1);
        scrollToTop();
      } else if (type === "document") {
        await addTaskDocumentComment(commentPayload);
        setRefreshCommentsTable((prev) => prev + 1);
        scrollToTop();
      }
      setLoading(false);
    } catch (error) {
      console.error("Error in saveComment:", error);
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: true,
        alertAction: "Cannot add comment",
        alertMessage: error.toString(),
        alertVariant: "danger",
      }));
    }
  };

  const handleFileCheck = (file) => {
    const alertInfo = checkInvalidFile(file);
    if (!alertInfo.validFile) {
      setAlertConfig((prev) => ({
        ...prev,
        showAlert: alertInfo.showAlert,
        alertAction: alertInfo.alertAction,
        alertMessage: alertInfo.alertMessage,
        alertVariant: alertInfo.alertVariant,
        sizeExceeded: alertInfo.sizeExceeded,
      }));
    } else {
      setAlertConfig((prev) => ({
        showAlert: false,
      }));
    }
    return alertInfo.validFile;
  };

  return (
    <>        
    <h4 className="heading mb-0">Comments</h4>
      <div className="dynamic-container p-0">
        <Form.Group className="comments mb-0">
          <TaskDynamicFields
            projectTypeId={projectTypeId}
            taskTypeId={taskTypeId}
            dynamicFields={dynamicFields}
            setDynamicFields={setDynamicFields}
            taskMembers={taskMembers}
            isFromCreate={assignID === undefined}
            disabled={disabled}
          />
          <h5 className="mb-2">Comments/Upload New File</h5>
          <MentionsInput
            name="description"
            value={newCommentText}
            onChange={(e) => {
              setNewCommentText(e.target.value);
            }}
            allowSuggestionsAboveCursor={true}
            allowSpaceInQuery={true}
            placeholder={"Enter Comment"}
            className="comments-textarea-new"
          >
            <Mention
              trigger="@"
              data={(query, callback) =>
                getUsersMatchingQuery(tags, query, callback)
              }
              appendSpaceOnAdd={true}
              renderSuggestion={MentionUserItem}
              markup="@[__display__]"
              displayTransform={mentionDisplayTransform}
              onAdd={handleAddTaggedUser}
            />
          </MentionsInput>
        </Form.Group>
        <Form.Row>
          <div
            className="attachment-container-std"
            onDrop={drop}
            onDragOver={dragOver}
            onDragEnter={dragEnter}
            onDragLeave={dragLeave}
          >
            <Form.Group className="upload-file-box">
              <Form.Control
                className="browse-btn-hidden"
                type="file"
                multiple
                onChange={(e) => {
                  browseFile(
                    e,
                    selectedFiles,
                    setSelectedFiles,
                    handleFileCheck
                  );
                }}
              />
              <Button className="browse-file-btn add-attachment-btn"></Button>
              {selectedFiles.map((file,index) => (
                <div className="single-file" key={`${file.name}_${index}`}>
                  <span className="file-name">
                    {shortenFileName(file.name)}
                  </span>
                  <Button
                    className="delete-file-btn"
                    onClick={(e) => {
                      deleteBrowseFile(
                        e,
                        file.name,
                        selectedFiles,
                        setSelectedFiles
                      );
                    }}
                  >
                    x
                  </Button>
                </div>
              ))}
            </Form.Group>
          </div>
        </Form.Row>
        {alertConfig.showAlert && alertConfig.alertVariant === "warning" && (
          <DisplayAlert
            showAlert={alertConfig.showAlert}
            alertMessage={alertConfig.alertMessage}
            sizeExceeded={alertConfig?.sizeExceeded}
          />
        )}
        {assignID && (
          <Form.Row style={{ marginLeft: 0 }}>
            <Button
              onClick={uploadFile}
              className="submit-btn-comments"
              variant="primary"
              disabled={isSubmitDisabled}
            >
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </Form.Row>
        )}
      </div>
    </>
  );
};

TaskCreateEditComments.propTypes = {
  projectTypeId: PropTypes.number.isRequired,
  taskTypeId: PropTypes.number.isRequired,
  dynamicFields: PropTypes.array.isRequired,
  setDynamicFields: PropTypes.func.isRequired,
  touched: PropTypes.object.isRequired,
  taskMembers: PropTypes.array.isRequired,
  newCommentText: PropTypes.string.isRequired,
  setNewCommentText: PropTypes.func.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  setSelectedFiles: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  assignID: PropTypes.number,
  type: PropTypes.string.isRequired,
  setAlertConfig: PropTypes.func.isRequired,
  alertConfig: PropTypes.object.isRequired,
  tags: PropTypes.array.isRequired,
  setRefreshCommentsTable: PropTypes.func.isRequired,
  setRefreshStatus: PropTypes.func.isRequired,
};

export default TaskCreateEditComments;
